// src/pages/CounselorCoupleDetailPage.js
// Updated to include both 20-Factor Assessment and 10-Module Intensive management

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, ClipboardList } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import CounselorFactorControl from '../components/CounselorFactorControl';
import CounselorIntensiveControl from '../components/CounselorIntensiveControl';
import LoadingSpinner from '../components/LoadingSpinner';

const CounselorCoupleDetailPage = () => {
  const { coupleId } = useParams();
  const navigate = useNavigate();
  
  console.log('=== COUNSELOR COUPLE DETAIL PAGE ===');
  console.log('Couple ID from URL:', coupleId);
  
  const [couple, setCouple] = useState(null);
  const [partner1Progress, setPartner1Progress] = useState(null);
  const [partner2Progress, setPartner2Progress] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Tab state: 'intensive' for 10-module, 'assessment' for 20-factor
  const [activeTab, setActiveTab] = useState('intensive');

  useEffect(() => {
    if (coupleId) {
      loadCoupleData();
    }
  }, [coupleId]);

  const loadCoupleData = async () => {
    try {
      setLoading(true);
      console.log('Loading couple data for:', coupleId);
      
      // Load couple document
      const coupleDoc = await getDoc(doc(db, 'couples', coupleId));
      
      if (!coupleDoc.exists()) {
        console.error('Couple not found');
        setLoading(false);
        return;
      }
      
      // FIXED: Spread data first, then set id to ensure document ID is correct
      const coupleData = { 
        ...coupleDoc.data(), 
        id: coupleDoc.id,           // This is the Firestore document ID
        docId: coupleDoc.id         // Backup reference
      };
      console.log('Loaded couple data:', coupleData);
      console.log('Document ID:', coupleDoc.id);
      setCouple(coupleData);
      
      // Load partner 1 assessment
      if (coupleData.partner1Id) {
        const partner1Doc = await getDoc(doc(db, 'assessments', coupleData.partner1Id));
        if (partner1Doc.exists()) {
          const p1Data = { id: partner1Doc.id, ...partner1Doc.data() };
          console.log('Partner 1 assessment:', p1Data);
          setPartner1Progress(p1Data);
        } else {
          console.log('Partner 1 has no assessment yet');
          setPartner1Progress(null);
        }
      }
      
      // Load partner 2 assessment
      if (coupleData.partner2Id) {
        const partner2Doc = await getDoc(doc(db, 'assessments', coupleData.partner2Id));
        if (partner2Doc.exists()) {
          const p2Data = { id: partner2Doc.id, ...partner2Doc.data() };
          console.log('Partner 2 assessment:', p2Data);
          setPartner2Progress(p2Data);
        } else {
          console.log('Partner 2 has no assessment yet');
          setPartner2Progress(null);
        }
      }
      
      console.log('✅ All data loaded successfully');
      setLoading(false);
    } catch (error) {
      console.error('Error loading couple data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!couple) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Couple Not Found</h2>
          <button
            onClick={() => navigate('/counselor/dashboard')}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Calculate progress for both systems
  const completedFactors = partner1Progress?.completedFactors?.length || 0;
  const completedModules = partner1Progress?.completedIntensiveModules?.length || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/counselor/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          
          <h1 className="text-2xl font-bold">
            {couple.partner1Name} & {couple.partner2Name}
          </h1>
          <p className="text-gray-600 text-sm">
            Manage assessment modules and view progress
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Tab Navigation */}
        <div className="mb-6 bg-white rounded-xl shadow-sm border p-1 flex">
          <button
            onClick={() => setActiveTab('intensive')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition ${
              activeTab === 'intensive'
                ? 'bg-emerald-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Award className="w-5 h-5" />
            <span>Pre-Marriage Intensive</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              activeTab === 'intensive' ? 'bg-emerald-500' : 'bg-gray-200'
            }`}>
              {completedModules}/10
            </span>
          </button>
          <button
            onClick={() => setActiveTab('assessment')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition ${
              activeTab === 'assessment'
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ClipboardList className="w-5 h-5" />
            <span>20-Factor Assessment</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              activeTab === 'assessment' ? 'bg-purple-500' : 'bg-gray-200'
            }`}>
              {completedFactors}/20
            </span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'intensive' ? (
          <CounselorIntensiveControl
            couple={couple}
            coupleDocId={coupleId}
            partner1Progress={partner1Progress}
            partner2Progress={partner2Progress}
            onUpdate={loadCoupleData}
          />
        ) : (
          <CounselorFactorControl
            couple={couple}
            coupleDocId={coupleId}  
            partner1Progress={partner1Progress}
            partner2Progress={partner2Progress}
            onUpdate={loadCoupleData}
          />
        )}
      </div>
    </div>
  );
};

export default CounselorCoupleDetailPage;
